import { createSlice } from '@reduxjs/toolkit';
import requestEndpoints from 'app/api/services/requestEndpoints';
import { EXTERNAL_CONTEXT } from 'app/config/urls';
import { hideLoading } from './settings';
import { preparedFormData } from 'app/utils/forms/functions';
import notification from 'app/config/notification';
import vehiclesEndpoint from 'app/api/services/vehiclesEndpoint';
import surveyEndpoint from 'app/api/services/surveyEndpoint';
import { errorHandler } from 'app/utils/errorHandler';
import { SERVICES_CODES } from 'app/config/services';
import licenseEndpoint from 'app/api/services/licenseEndpoint';
import lineEndpoint from 'app/api/services/lineEndpoint';

const initialState = {
    requests: [],
    newRequest: {},
    details: {},
};

const requestsSlice = createSlice({
    name: 'requests',
    initialState,
    reducers: {
        getRequests(state, action) {
            state.requests = action.payload;
        },
        clearRequests(state) {
            state.requests = initialState.requests;
        },
        getNewRequest(state, action) {
            state.newRequest = action.payload;
        },
        clearNewRequest(state) {
            state.details = initialState.newRequest;
        },
        updateRequestDetails(state, action) {
            state.details = action.payload;
        },
        clearRequestDetails(state) {
            state.details = initialState.details;
        },
    },
});

export const fetchNewRequest =
    (guest = false, params) =>
    async (dispatch) => {
        let response;
        let context = EXTERNAL_CONTEXT;
        if (guest) context = '/guest';
        response = await requestEndpoints.getNewRequest(params, context);
        dispatch(getNewRequest(response?.data));
    };

export const fetchRequests = (params, context) => async (dispatch) => {
    let response = await requestEndpoints.getRequests(params, context);
    dispatch(getRequests(response.data));
};

export const fetchRequest = (params, context) => async (dispatch) => {
    let response = await requestEndpoints.findRequest(params, context);
    dispatch(updateRequestDetails(response.data));
};

export const fetchSaveRequest = async ({
    isFormData,
    preparedPayload,
    dispatch,
    intl,
    showMessage,
    cdService,
    context = EXTERNAL_CONTEXT,
}) => {
    let response = {};
    try {
        let sendingData = preparedPayload.payload;
        let message = intl.formatMessage({ id: 'MESSAGES.SERVICE.FORMS.SUCCESSFUL_SAVE_REQUEST' });
        if (isFormData) sendingData = preparedFormData(preparedPayload);

        if (context !== EXTERNAL_CONTEXT) {
            response = await requestEndpoints.saveRequest(sendingData, context);
        } else {
            // eslint-disable-next-line default-case
            switch (cdService) {
                case SERVICES_CODES.ACESSO_PORTAL_FISICA:
                case SERVICES_CODES.ACESSO_PORTAL_JURIDICA:
                    if (!preparedPayload.payload.cdSolicitacao) {
                        response = await requestEndpoints.saveRequestAccessPortal(sendingData);
                        message = intl.formatMessage({ id: 'MESSAGES.SERVICE.FORMS.SUCCESSFUL_REQUEST' });
                    } else {
                        response = await requestEndpoints.saveRequest(sendingData, context);
                    }
                    break;

                case SERVICES_CODES.CHANGE_EMAIL:
                    if (!preparedPayload.payload.cdSolicitacao) {
                        response = await requestEndpoints.saveRequestChangeEmail(sendingData);
                        let numeroSolicitacao = '';
                        if (response?.data) numeroSolicitacao = `Solicitação ${response?.data.nuSolicitacaoFmt}.`;
                        message = intl.formatMessage(
                            { id: 'MESSAGES.SERVICE.FORMS.SUCCESSFUL_REQUEST_CHANGE_EMAIL' },
                            { numeroSolicitacao },
                        );
                    } else {
                        response = await requestEndpoints.saveRequest(sendingData, context);
                    }
                    break;

                case SERVICES_CODES.INSCRICAO_CADASTRAL:
                case SERVICES_CODES.RENOVACAO_CADASTRAL:
                case SERVICES_CODES.ATUALIZACAO_CADASTRAL_COM_CRC:
                case SERVICES_CODES.ATUALIZACAO_CADASTRAL_SEM_CRC:
                case SERVICES_CODES.SEGUNDA_VIA_CRC:
                case SERVICES_CODES.INATIVACAO_CADASTRAL:
                    response = await requestEndpoints.saveRequest(sendingData, context);
                    break;

                case SERVICES_CODES.INCLUSAO_VEICULOS_SEM_CRC:
                case SERVICES_CODES.INCLUSAO_VEICULOS:
                case SERVICES_CODES.ALTERACAO_VEICULOS:
                case SERVICES_CODES.EXCLUSAO_VEICULOS:
                case SERVICES_CODES.ALTERACAO_VEICULOS_SEM_CRC:
                    response = await vehiclesEndpoint.saveRequest(sendingData, context);
                    break;

                case SERVICES_CODES.VISTORIA:
                case SERVICES_CODES.LAUDO_VISTORIA:
                    response = await surveyEndpoint.saveRequest(sendingData, context);
                    break;

                case SERVICES_CODES.LICENCA_VIAGEM_EVENTUAL:
                case SERVICES_CODES.LICENCA_VINCULADA:
                case SERVICES_CODES.LICENCA_FRETAMENTO:
                case SERVICES_CODES.LICENCA_FRETAMENTO_TRATAMENTO_SAUDE:
                case SERVICES_CODES.LICENCA_FRETAMENTO_UNIVERSITARIOS:
                case SERVICES_CODES.LICENCA_ESCOLAR:
                case SERVICES_CODES.LICENCA_TURISMO:
                case SERVICES_CODES.LICENCA_SEGUNDA_VIA:
                    response = await licenseEndpoint.saveRequest(sendingData, context);
                    break;

                case SERVICES_CODES.SLIC_MODIFICACAO_HORARIOS:
                case SERVICES_CODES.SLIC_AMPLIACAO_HORARIOS:
                case SERVICES_CODES.SLIC_SUPRESSAO_HORARIOS:
                case SERVICES_CODES.SLIC_SUBSTITUICAO_VEICULO:
                case SERVICES_CODES.SLIC_PRORROGACAO_PRAZO:
                case SERVICES_CODES.SLIC_TRANSFERENCIA_PERMISSAO:
                case SERVICES_CODES.SLIC_CANCELAMENTO_PERMISSAO:
                case SERVICES_CODES.SLIC_INCLUSAO_CONDUTOR_SUBSTITUTO:
                case SERVICES_CODES.STRIP_PROLONGAMENTO_LINHA:
                case SERVICES_CODES.STRIP_ENCURTAMENTO_LINHA:
                case SERVICES_CODES.STRIP_REFORCO_HORARIOS:
                case SERVICES_CODES.STRIP_CONEXAO_LINHAS:
                case SERVICES_CODES.STRIP_ALTERACAO_DEFINITIVA_ITINERARIO:
                case SERVICES_CODES.STRIP_ALTERACAO_PROVISORIA_ITINERARIO:
                case SERVICES_CODES.STRIP_INCLUSAO_TIPO_EQUIPAMENTO:
                case SERVICES_CODES.STRIP_SUBSTITUICAO_TIPO_EQUIPAMENTO:
                case SERVICES_CODES.STRIP_MODIFICACAO_HORARIOS:
                case SERVICES_CODES.STRIP_AMPLIACAO_HORARIOS:
                case SERVICES_CODES.STRIP_SUPRESSAO_HORARIOS:
                case SERVICES_CODES.STRIP_IMPLANTACAO_SECAO:
                case SERVICES_CODES.STRIP_SUPRESSAO_SECAO:
                case SERVICES_CODES.STRIP_CANCELAMENTO_LINHA:
                case SERVICES_CODES.STRIP_CANCELAMENTO_SERVICO:
                case SERVICES_CODES.STRIP_PRORROGACAO_PRAZO:
                case SERVICES_CODES.STRIP_TRANSFERENCIA_LINHA:
                    response = await lineEndpoint.saveRequest(sendingData, context);
                    break;
            }
        }

        if (response.status === 200) {
            if (showMessage) {
                notification(message, {
                    toastId: 'SUCCESSFUL_SAVE_REQUEST',
                    type: 'success',
                });
            }
        }
    } catch (error) {
        dispatch(hideLoading());
        errorHandler(error.response?.status, error.response?.data?.message);
    }
    return response;
};

export const { getRequests, clearRequests, getNewRequest, clearNewRequest, updateRequestDetails, clearRequestDetails } =
    requestsSlice.actions;

export default requestsSlice;
